import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, User, Activity, FileText, AlertTriangle, File, Target, Clock } from 'lucide-react';

const TYPE_CONFIG = {
  snapshot_update: { icon: User, color: 'text-violet-600', bg: 'bg-violet-100', label: 'Snapshot' },
  chart: { icon: Activity, color: 'text-sky-600', bg: 'bg-sky-100', label: 'Chart' },
  note: { icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Note' },
  incident: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100', label: 'Incident' },
  document: { icon: File, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Document' },
  careplan: { icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Care Plan' },
};

const FILTERS = [
  { value: 'ALL', label: 'All Activity' },
  { value: 'snapshot_update', label: 'Snapshot Updates' },
  { value: 'chart', label: 'Chart Entries' },
  { value: 'note', label: 'Notes' },
  { value: 'incident', label: 'Incidents' },
  { value: 'document', label: 'Documents' },
  { value: 'careplan', label: 'Care Plans' },
];

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

export function ParticipantUnifiedTimeline({ participantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchTimeline = useCallback(async (resetPage = false) => {
    const p = resetPage ? 1 : page;
    if (resetPage) setPage(1);
    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (filter && filter !== 'ALL') params.append('type', filter);
      const res = await api.get(`/participants/${participantId}/unified-timeline?${params}`);
      const data = res.data.data;
      const newItems = data.items || [];
      setItems(prev => (resetPage || p === 1) ? newItems : [...prev, ...newItems]);
      setHasMore(data.pagination?.has_more || false);
      setTotal(data.pagination?.total || 0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [participantId, filter, page]);

  useEffect(() => { fetchTimeline(true); }, [participantId, filter]);
  useEffect(() => { if (page > 1) fetchTimeline(); }, [page]);

  return (
    <div className="space-y-4" data-testid="unified-timeline">
      {/* Header + Filter */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Activity Timeline</h3>
          {total > 0 && <p className="text-xs text-muted-foreground">{total} events</p>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="timeline-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => fetchTimeline(true)}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12" data-testid="timeline-loading">
          <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading timeline...</span>
        </div>
      ) : error ? (
        <Card data-testid="timeline-error">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-destructive">Failed to load timeline</p>
            <Button variant="outline" size="sm" onClick={() => fetchTimeline(true)} className="mt-2">Retry</Button>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card data-testid="timeline-empty">
          <CardContent className="py-10 text-center">
            <Clock className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No activity available</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Timeline Items */}
          <div className="relative" data-testid="timeline-items">
            {/* Vertical line */}
            <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />

            <div className="space-y-0">
              {items.map((item, idx) => {
                const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.note;
                const Icon = cfg.icon;
                return (
                  <div key={item.id || idx} className="relative flex gap-3 py-3 pl-1" data-testid={`timeline-item-${idx}`}>
                    {/* Icon dot */}
                    <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full ${cfg.bg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{item.title}</span>
                        <Badge variant="outline" className="text-[9px] px-1 py-0">{cfg.label}</Badge>
                        {item.meta?.severity && (
                          <Badge variant={item.meta.severity === 'high' ? 'destructive' : 'secondary'} className="text-[9px] px-1 py-0">
                            {item.meta.severity}
                          </Badge>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                        <span>{timeAgo(item.timestamp)}</span>
                        {item.created_by?.name && (
                          <>
                            <span className="text-muted-foreground/40">·</span>
                            <span>{item.created_by.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center pt-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} data-testid="timeline-load-more">
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
